import { useState } from 'react'
import type { GridPlacement } from '../../types';
import { GridLetter } from './GridLetter'
import { dragging, gridIssueBadge, gridIssueTooltip } from '../../lib/Z'

type GridWordProps = {
  text: string | undefined
  wordLength: number
  isSelected: boolean
  isDragging: boolean
  isHighlighted?: boolean | undefined
  issueCount?: number | undefined
  issueTooltip?: string | undefined
  XY: { x: number; y: number; }
  placement: GridPlacement
  cellSize: number
  inverted?: boolean | undefined
  conflictIndices?: number[] | undefined
  isPreview?: boolean | undefined
  editableIndex?: number | null | undefined // Which letter index is editable and focused
  isTemp?: boolean | undefined // This word (A or B side) is temporary and not saved to word list
  onLetterInput?: ((index: number, value: string) => void) | undefined
}

/**
 * Word rendered as absolutely positioned letters within a container.
 * The container itself is not positioned - parent handles placement.
 */
export function GridWord({
  isSelected,
  isDragging,
  isHighlighted = false,
  issueCount = 0,
  issueTooltip,
  XY,
  placement,
  text,
  cellSize,
  inverted = false,
  conflictIndices = [],
  isPreview = false,
  wordLength,
  editableIndex = null,
  isTemp = false,
  onLetterInput,
  ...props
}: GridWordProps & React.HTMLAttributes<HTMLDivElement>) {

  const [issueHover, setIssueHover] = useState(false)

  const letters = text?.replace(/\s/g, '') ?? Array(wordLength).fill(' ').join('')
  const conflictSet = new Set(conflictIndices)

  // Container dimensions
  const width = letters.length * cellSize
  const height = cellSize

  const isHorizontal = placement.direction === 'across'

  return (
    <div
      key={placement.id}
      data-grid-placement
      className={`absolute ${isPreview ? 'pointer-events-none' : 'cursor-grab active:cursor-grabbing'}`}
      style={{
        left: XY.x,
        top: XY.y,
        width: isHorizontal ? width + (letters.length * 1) - 1 : height,
        height: isHorizontal ? height : width + (letters.length * 1) - 1,
        willChange: isDragging ? 'transform' : 'auto',
        zIndex: isDragging ? dragging : isPreview ? 3 : undefined,
        touchAction: 'none', // Prevent browser touch gestures
      }}
      {...props}    >
      {issueCount > 0 && !isPreview && (
        <div
          className="absolute inset-0 pointer-events-none overflow-hidden"
          style={{
            background: 'radial-gradient(circle at top left, rgba(252,211,77,0.25) 0%, rgba(252,211,77,0.08) 40%, transparent 70%)',
          }}
        />
      )}
      {issueCount > 0 && !isPreview && (
        <span
          className="absolute -top-1.5 -left-1.5 min-w-3.5 h-3.5 px-0.5 rounded-full bg-amber-300 text-amber-900 text-[9px] leading-3.5 text-center font-semibold cursor-default"
          style={{ zIndex: gridIssueBadge }}
          onMouseEnter={() => setIssueHover(true)}
          onMouseLeave={() => setIssueHover(false)}
        >
          !
          {issueHover && issueTooltip && (
            <div
              className="absolute bottom-full left-0 mb-1.5 px-2.5 py-1.5 rounded-md bg-gray-900 text-white text-[11px] leading-relaxed font-normal text-left whitespace-pre-line w-max max-w-56 shadow-lg pointer-events-none"
              style={{ zIndex: gridIssueTooltip }}
            >
              {issueTooltip}
            </div>
          )}
        </span>
      )}

      {letters.split('').map((letter, idx) => {
        // Only the focused cell should be editable (and only if it's a placeholder or temp word)
        const isFocusedCell = editableIndex === idx
        const canEdit = isFocusedCell && (letter === '□' || isTemp) && !isPreview
        return (
          <GridLetter
            key={idx}
            letter={letter}
            x={isHorizontal ? idx * cellSize + (idx * 1) : 0}
            y={isHorizontal ? 0 : idx * cellSize + (idx * 1)}
            size={cellSize}
            isConflict={conflictSet.has(idx)}
            isSelected={isSelected}
            isHighlighted={isHighlighted}
            isPreview={isPreview}
            isEditable={canEdit}
            isFocused={isFocusedCell && canEdit}
            isTemp={isTemp}
            onInput={canEdit ? (value) => onLetterInput?.(idx, value) : undefined}
          />
        )
      })}
      {/* <div className={`absolute pointer-events-none inset-0 ${isSelected ? 'outline outline-blue-500' : ''}`}></div> */}
    </div>
  )
}
